import { Assets } from 'pixi.js';
import { PiggyBankOverlayView } from '../views/PiggyBankOverlayView';
import type { PiggyBankDependencies, PiggyBankTier } from '../types';
import {
  decreasePiggyBankTierIfNeeded,
  getPiggyBankState,
  increasePiggyBankTier,
  isPiggyBankExpired,
  isPiggyBankFull,
} from '../PiggyBankUtils';
import { preloadLayoutsAssets } from '../../../views/preloadLayoutsAssets';
import { LoadingView } from '../../../views/LoadingView';
import { PIGGY_BANK_TIER_CONFIG_MAP } from '../PiggyBankConstants';

interface OpenPiggyBankConfig {
  dependencies: PiggyBankDependencies;
  onPlayClicked: () => void | Promise<void>;
  onPiggyBankTierIncreased: () => void | Promise<void>;
}

async function loadPiggyBankAssets(tier: PiggyBankTier): Promise<boolean> {
  const { assetBundle } = PIGGY_BANK_TIER_CONFIG_MAP[tier];
  const isBundleLoaded = Assets.cache.has(assetBundle);
  if (isBundleLoaded) return true;

  void LoadingView.getInstance().show();
  if (!(await preloadLayoutsAssets(PiggyBankOverlayView.requiredLayouts, false))) return false;
  await Assets.loadBundle(assetBundle);
  await LoadingView.getInstance().hide();

  return true;
}

export async function openPiggyBankOverlay(config: OpenPiggyBankConfig): Promise<void> {
  const { dependencies, onPlayClicked, onPiggyBankTierIncreased } = config;
  const { observable, overlayContainer, purchasePiggyBank, getPiggyBankPrice, watchAdForPiggyBank } = dependencies;

  const piggyBankState = getPiggyBankState(observable);

  if (!(await loadPiggyBankAssets(piggyBankState.currentTier))) return Promise.resolve();

  let isLastChance = false;
  if (isPiggyBankExpired(observable)) {
    if (isPiggyBankFull(observable)) {
      isLastChance = true;
    } else {
      decreasePiggyBankTierIfNeeded(observable);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  const { resolve, promise } = Promise.withResolvers<void>();

  const { coins, levelIndex } = observable.getValue();

  const piggyBankOverlay = new PiggyBankOverlayView({
    piggyBankState,
    levelNumber: levelIndex + 1,
    coins,
    isLastChance,
    getPiggyBankPrice: (): Promise<string | null> => getPiggyBankPrice(piggyBankState.currentTier),
  });

  overlayContainer.addChild(piggyBankOverlay);

  // attempt to purchase piggy bank offer.
  piggyBankOverlay.onClaimPiggyBank.connect(() => {
    const piggyBankPromise = watchAdForPiggyBank || purchasePiggyBank;
    piggyBankPromise(piggyBankState.currentTier)
      .then(async (purchaseResult): Promise<void> => {
        if (purchaseResult.success) {
          const coinReward = purchaseResult?.reward?.Coin ?? 0;
          await piggyBankOverlay.runClaimSequence(coinReward);
          await piggyBankOverlay.close();
          if (increasePiggyBankTier(observable)) {
            await onPiggyBankTierIncreased();
          }
          resolve();
        }
      })
      .catch((error) => {
        console.error(error);
      });
  });

  piggyBankOverlay.onPlayClicked.connect(async () => {
    await piggyBankOverlay.close();
    await onPlayClicked();
    resolve();
  });

  piggyBankOverlay.onCloseClicked.connect(() => {
    void piggyBankOverlay.close();

    if (isPiggyBankExpired(observable)) {
      decreasePiggyBankTierIfNeeded(observable);
    }
    resolve();
  });

  return promise;
}
